import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#0F4C3A] hover:text-[#0B3C2E] font-semibold transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Privacy Policy
          </h1>
          <p className="text-[#0F4C3A] font-semibold mb-8">Effective Date: May 2026</p>

          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="text-slate-500 italic mb-8">
              This Privacy Policy elucidates the practices regarding the collection, processing, storage, and transfer of personal data as mandated by the Digital Personal Data Protection Act, 2023 (DPDP Act) and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>1. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"Data Principal"</strong> refers to You, the User interacting with the Platform.</li>
                <li><strong>"Data Fiduciary"</strong> refers to Project Maa, the entity determining the purpose and means of processing personal data.</li>
                <li><strong>"Data Processor"</strong> refers to third-party entities (e.g., ElevenLabs) processing data on behalf of the Data Fiduciary.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>2. Consent and Lawful Basis for Processing</h2>
              <p>
                By initiating a voice interaction with the Platform, the Data Principal provides explicit, affirmative consent for the collection and processing of their voice data and associated health-related information for the purposes specified herein.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>3. Data Collection and Purpose Limitation</h2>
              <p className="mb-4">The Data Fiduciary collects the following categories of data:</p>
              
              <div className="bg-[#0F4C3A]/10 rounded-xl p-4 mb-4">
                <ul className="space-y-2">
                  <li><strong>Audio Data & Transcripts:</strong> Real-time voice inputs and subsequent text conversions.</li>
                  <li><strong>Volunteered Health Data:</strong> Information voluntarily disclosed by the User regarding pregnancy or pediatric health.</li>
                  <li><strong>Technical Metadata:</strong> IP addresses, timestamps, and browser configurations.</li>
                </ul>
              </div>

              <p className="mb-4">The data is processed strictly for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Fulfillment:</strong> Facilitating real-time, low-latency conversational responses utilizing third-party Data Processors.</li>
                <li><strong>Algorithmic Training (Machine Learning):</strong> Transcripts derived from user calls are explicitly utilized to train, fine-tune, and improve the AI model's accuracy, empathy, and contextual understanding.</li>
                <li><strong>Quality Assurance:</strong> Internal auditing to ensure compliance with medical safety standards.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>4. Data Anonymization and Protection</h2>
              <p>
                Prior to the utilization of any conversational data for AI model training, the Data Fiduciary undertakes rigorous anonymization protocols to permanently strip Personally Identifiable Information (PII) from the transcripts, ensuring the Data Principal cannot be reasonably identified from the training dataset.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>5. Third-Party Data Processors</h2>
              <p>
                The Platform utilizes specialized Data Processors (including, but not limited to, ElevenLabs) for Speech-to-Text (STT) and Text-to-Speech (TTS) functionalities. These Data Processors are contractually bound to process the audio data solely for the provision of the requested service and are prohibited from retaining the audio data for independent commercial exploitation or training of their proprietary models outside the scope of the agreement with Project Maa.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>6. Rights of the Data Principal</h2>
              <p className="mb-4">Subject to the provisions of the DPDP Act, 2023, the Data Principal possesses the following rights:</p>
              
              <div className="bg-[#C86D51]/10 rounded-xl p-4">
                <ul className="space-y-3">
                  <li><strong>Right to Access:</strong> To obtain confirmation regarding the processing of personal data and a summary thereof.</li>
                  <li><strong>Right to Correction and Erasure:</strong> To request the rectification of inaccurate data or the deletion of conversational history.</li>
                  <li><strong>Right to Withdraw Consent:</strong> To withdraw consent at any time, acknowledging that such withdrawal may result in the termination of the User's ability to utilize the voice-first service.</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>7. Governing Law and Jurisdiction</h2>
              <p>
                This Privacy Policy shall be governed by and construed strictly in accordance with the laws of the Republic of India. Any legal suit, action, or proceeding arising out of or relating to this policy shall be instituted exclusively in the competent courts located in New Delhi, Delhi, India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>8. Grievance Redressal</h2>
              <p>
                In accordance with the Information Technology Act, 2000 and the DPDP Act, 2023, if You have any grievances regarding the processing of Your personal data, You may contact the designated Grievance Officer at the official contact email provided on the Project Maa website.
              </p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
